<template>
  <div>
    <section class="flex flex-col md:flex-row md:items-center justify-between mb-4">
      <!-- Left side: Filters and Sort -->
      <div class="grid grid-cols-2 md:flex items-center space-x-4">
        <!-- Filters Dropdown -->
        <div class="relative">
          <button
            @click="toggleFilters"
            class="w-full md:w-auto flex items-center space-x-3 bg-white text-gray-700 px-5 py-3 rounded-lg border border-gray-300 hover:border-gray-400 transition-colors"
          >
            <Filter class="w-4 h-4 text-gray-500" />
            <span class="font-medium">Filters</span>
            <span
              v-if="totalActiveFilters > 0"
              class="bg-blue-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center"
            >
              {{ totalActiveFilters }}
            </span>
            <ChevronDown class="w-4 h-4 text-gray-400" />
          </button>

          <div
            v-show="showFiltersDropdown"
            class="absolute top-full left-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-200 p-5 z-10"
          >
            <div class="flex items-center justify-between mb-4">
              <h3 class="font-bold text-gray-900 text-base">Filter Options</h3>
              <button
                v-if="totalActiveFilters > 0"
                @click="clearFilters"
                class="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear All
              </button>
            </div>

            <div class="space-y-1">

              <!-- Subject filter accordion -->
              <div>
                <button
                  @click="isSubjectOpen = !isSubjectOpen"
                  class="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <p class="text-sm font-semibold text-gray-700">Subjects</p>
                  <ChevronDown
                    class="w-4 h-4 text-gray-600 transition-transform duration-200"
                    :class="{ 'rotate-180': isSubjectOpen }"
                  />
                </button>
                <div v-if="activeFilters.subjects.length > 0" class="px-2.5 pt-1 pb-1 flex flex-wrap gap-1.5">
                  <span
                    v-for="slug in activeFilters.subjects"
                    :key="`sel-subj-${slug}`"
                    class="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md text-xs font-medium"
                  >
                    {{ findName(subjects, slug) }}
                    <button @click="removeFilter('subjects', slug)" class="ml-1 hover:text-blue-900">
                      <X class="w-3 h-3" />
                    </button>
                  </span>
                </div>
                <Transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="transform opacity-0 -translate-y-2"
                  enter-to-class="transform opacity-100 translate-y-0"
                  leave-active-class="transition duration-150 ease-in"
                  leave-from-class="transform opacity-100 translate-y-0"
                  leave-to-class="transform opacity-0 -translate-y-2"
                >
                  <div v-show="isSubjectOpen" class="pt-1 space-y-1 max-h-48 overflow-y-auto scrollbar-hover">
                    <label
                      v-for="subject in sortByName(subjects)"
                      :key="subject.slug"
                      class="flex items-center px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        :value="subject.slug"
                        :checked="isFilterActive('subjects', subject.slug)"
                        @change="toggleFilter('subjects', subject.slug)"
                        class="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span class="text-sm text-gray-700 ml-3">{{ subject.name }}</span>
                    </label>
                  </div>
                </Transition>
              </div>

              <!-- Level filter accordion -->
              <div class="border-t border-gray-200 pt-1">
                <button
                  @click="isLevelOpen = !isLevelOpen"
                  class="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <p class="text-sm font-semibold text-gray-700">Levels</p>
                  <ChevronDown
                    class="w-4 h-4 text-gray-600 transition-transform duration-200"
                    :class="{ 'rotate-180': isLevelOpen }"
                  />
                </button>
                <div v-if="activeFilters.levels.length > 0" class="px-2.5 pt-1 pb-1 flex flex-wrap gap-1.5">
                  <span
                    v-for="slug in activeFilters.levels"
                    :key="`sel-lvl-${slug}`"
                    class="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md text-xs font-medium"
                  >
                    {{ findName(levels, slug) }}
                    <button @click="removeFilter('levels', slug)" class="ml-1 hover:text-blue-900">
                      <X class="w-3 h-3" />
                    </button>
                  </span>
                </div>
                <Transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="transform opacity-0 -translate-y-2"
                  enter-to-class="transform opacity-100 translate-y-0"
                  leave-active-class="transition duration-150 ease-in"
                  leave-from-class="transform opacity-100 translate-y-0"
                  leave-to-class="transform opacity-0 -translate-y-2"
                >
                  <div v-show="isLevelOpen" class="pt-1 space-y-1 max-h-48 overflow-y-auto scrollbar-hover">
                    <label
                      v-for="level in sortByName(levels)"
                      :key="level.slug"
                      class="flex items-center px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        :value="level.slug"
                        :checked="isFilterActive('levels', level.slug)"
                        @change="toggleFilter('levels', level.slug)"
                        class="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span class="text-sm text-gray-700 ml-3">{{ level.name }}</span>
                    </label>
                  </div>
                </Transition>
              </div>

              <!-- Exam board filter accordion -->
              <div class="border-t border-gray-200 pt-1">
                <button
                  @click="isExamboardOpen = !isExamboardOpen"
                  class="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <p class="text-sm font-semibold text-gray-700">Exam Boards</p>
                  <ChevronDown
                    class="w-4 h-4 text-gray-600 transition-transform duration-200"
                    :class="{ 'rotate-180': isExamboardOpen }"
                  />
                </button>
                <div v-if="activeFilters.examboards.length > 0" class="px-2.5 pt-1 pb-1 flex flex-wrap gap-1.5">
                  <span
                    v-for="slug in activeFilters.examboards"
                    :key="`sel-eb-${slug}`"
                    class="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md text-xs font-medium"
                  >
                    {{ findName(examboards, slug) }}
                    <button @click="removeFilter('examboards', slug)" class="ml-1 hover:text-blue-900">
                      <X class="w-3 h-3" />
                    </button>
                  </span>
                </div>
                <Transition
                  enter-active-class="transition duration-200 ease-out"
                  enter-from-class="transform opacity-0 -translate-y-2"
                  enter-to-class="transform opacity-100 translate-y-0"
                  leave-active-class="transition duration-150 ease-in"
                  leave-from-class="transform opacity-100 translate-y-0"
                  leave-to-class="transform opacity-0 -translate-y-2"
                >
                  <div v-show="isExamboardOpen" class="pt-1 space-y-1 max-h-48 overflow-y-auto scrollbar-hover">
                    <label
                      v-for="examboard in sortByName(examboards)"
                      :key="examboard.slug"
                      class="flex items-center px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        :value="examboard.slug"
                        :checked="isFilterActive('examboards', examboard.slug)"
                        @change="toggleFilter('examboards', examboard.slug)"
                        class="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span class="text-sm text-gray-700 ml-3">{{ examboard.name }}</span>
                    </label>
                  </div>
                </Transition>
              </div>

            </div>
          </div>
        </div>

        <!-- Sort Dropdown -->
        <div class="relative">
          <button
            @click="toggleSort"
            class="w-full md:w-auto flex items-center space-x-3 bg-white text-gray-700 px-5 py-3 rounded-lg border border-gray-300 hover:border-gray-400 transition-colors"
          >
            <ArrowUpDown class="w-4 h-4 text-gray-500" />
            <span class="font-medium md:hidden">Sort</span>
            <span class="font-medium hidden md:inline">
              Sort by: <span class="text-brand-blue">{{ currentSortLabel }}</span>
            </span>
            <ChevronDown class="w-4 h-4 text-gray-400" />
          </button>

          <div
            v-show="showSortDropdown"
            class="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 p-2 z-10"
          >
            <button
              v-for="option in sortOptions"
              :key="option.value"
              @click="selectSort(option.value)"
              :class="[
                'w-full text-left px-4 py-3 rounded-lg flex items-center justify-between transition-colors',
                currentSort === option.value
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-50'
              ]"
            >
              <span class="flex items-center">
                <component :is="option.icon" :class="['w-4 h-4 mr-3', option.iconColor]" />
                {{ option.label }}
              </span>
              <Check v-if="currentSort === option.value" class="w-4 h-4 text-blue-600" />
            </button>
          </div>
        </div>
      </div>

      <!-- Right side: Results count and view toggle -->
      <div class="flex items-center space-x-3 mt-4 md:mt-0">
        <span class="text-gray-600 text-sm">
          Showing <span class="font-semibold text-gray-900">{{ totalCount }}</span> tutors
        </span>
        <div class="hidden md:flex items-center space-x-2 bg-white border border-gray-300 rounded-lg p-1">
          <button
            @click="setView('grid')"
            :class="[
              'px-3 py-1.5 rounded-md transition-colors',
              currentView === 'grid'
                ? 'bg-brand-blue text-white'
                : 'text-gray-600 hover:bg-gray-100'
            ]"
          >
            <Grid3x3 class="w-4 h-4" />
          </button>
          <button
            @click="setView('list')"
            :class="[
              'px-3 py-1.5 rounded-md transition-colors',
              currentView === 'list'
                ? 'bg-brand-blue text-white'
                : 'text-gray-600 hover:bg-gray-100'
            ]"
          >
            <List class="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>

    <!-- Active filter tags (shown below the toolbar) -->
    <div v-if="totalActiveFilters > 0" class="flex flex-wrap items-center gap-2 mb-4">
      <span class="text-xs font-medium text-gray-500">Active filters:</span>
      <span
        v-for="slug in activeFilters.subjects"
        :key="`tag-subj-${slug}`"
        class="inline-flex items-center px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
      >
        {{ findName(subjects, slug) }}
        <button @click="removeFilter('subjects', slug)" class="ml-1.5 hover:text-blue-900">
          <X class="w-3 h-3" />
        </button>
      </span>
      <span
        v-for="slug in activeFilters.levels"
        :key="`tag-lvl-${slug}`"
        class="inline-flex items-center px-2.5 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium"
      >
        {{ findName(levels, slug) }}
        <button @click="removeFilter('levels', slug)" class="ml-1.5 hover:text-green-900">
          <X class="w-3 h-3" />
        </button>
      </span>
      <span
        v-for="slug in activeFilters.examboards"
        :key="`tag-eb-${slug}`"
        class="inline-flex items-center px-2.5 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium"
      >
        {{ findName(examboards, slug) }}
        <button @click="removeFilter('examboards', slug)" class="ml-1.5 hover:text-purple-900">
          <X class="w-3 h-3" />
        </button>
      </span>
      <button
        @click="clearFilters"
        class="text-xs text-gray-500 hover:text-gray-700 underline ml-1"
      >
        Clear all
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Filter, ChevronDown, ArrowUpDown, Star, PoundSterling, Users, Clock, Check, Grid3x3, List, X } from 'lucide-vue-next';

const props = defineProps({
  totalCount: {
    type: Number,
    default: 0
  },
  initialView: {
    type: String,
    default: 'grid',
    validator: (value) => ['grid', 'list'].includes(value)
  },
  initialSort: {
    type: String,
    default: 'popular'
  },
  subjects: {
    type: Array,
    default: () => []
  },
  levels: {
    type: Array,
    default: () => []
  },
  examboards: {
    type: Array,
    default: () => []
  },
  initialSubjects: {
    type: Array,
    default: () => []
  },
  initialLevels: {
    type: Array,
    default: () => []
  },
  initialExamboards: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['filter-change', 'sort-change', 'view-change']);

const showFiltersDropdown = ref(false);
const showSortDropdown = ref(false);
const currentView = ref(props.initialView);
const currentSort = ref(props.initialSort);

const isSubjectOpen = ref(true);
const isLevelOpen = ref(true);
const isExamboardOpen = ref(false);

const activeFilters = ref({
    subjects: [...(props.initialSubjects || [])],
    levels: [...(props.initialLevels || [])],
    examboards: [...(props.initialExamboards || [])],
});

const totalActiveFilters = computed(() => {
  return activeFilters.value.subjects.length
    + activeFilters.value.levels.length
    + activeFilters.value.examboards.length;
});

const sortByName = (items) => {
  return [...items].sort((a, b) => a.name.localeCompare(b.name));
};

const findName = (items, slug) => {
  return items.find(i => i.slug === slug)?.name || slug;
};

const sortOptions = [
  { value: 'rating', label: 'Highest Rated', icon: Star, iconColor: 'text-yellow-500' },
  { value: 'price', label: 'Lowest Price', icon: PoundSterling, iconColor: 'text-green-500' },
  { value: 'popular', label: 'Most Popular', icon: Users, iconColor: 'text-blue-500' },
  { value: 'newest', label: 'Newest', icon: Clock, iconColor: 'text-purple-500' },
];

const currentSortLabel = computed(() => {
  const option = sortOptions.find(opt => opt.value === currentSort.value);
  return option ? option.label : 'Most Popular';
});

const closeAllDropdowns = () => {
  showFiltersDropdown.value = false;
  showSortDropdown.value = false;
};

const toggleFilters = () => {
  const wasOpen = showFiltersDropdown.value;
  closeAllDropdowns();
  showFiltersDropdown.value = !wasOpen;
};

const toggleSort = () => {
  const wasOpen = showSortDropdown.value;
  closeAllDropdowns();
  showSortDropdown.value = !wasOpen;
};

const isFilterActive = (category, value) => {
  return activeFilters.value[category].includes(value);
};

const toggleFilter = (category, value) => {
  if (isFilterActive(category, value)) {
    activeFilters.value[category] = activeFilters.value[category].filter(item => item !== value);
  } else {
    activeFilters.value[category].push(value);
  }
  emit('filter-change', { ...activeFilters.value });
};

const removeFilter = (category, value) => {
  activeFilters.value[category] = activeFilters.value[category].filter(item => item !== value);
  emit('filter-change', { ...activeFilters.value });
};

const clearFilters = () => {
  activeFilters.value = {
    subjects: [],
    levels: [],
    examboards: [],
  };
  emit('filter-change', { ...activeFilters.value });
};

const selectSort = (value) => {
  currentSort.value = value;
  showSortDropdown.value = false;
  emit('sort-change', value);
};

const setView = (view) => {
  currentView.value = view;
  emit('view-change', view);
};
</script>
